import React, { useContext, useEffect, useState } from 'react'
import { Link, Redirect, useHistory, useLocation, useParams } from 'react-router-dom'
import { INLINE_MESSAGE_VARIANTS, InlineMessage } from 'transfix-ui/components/InlineMessage'
import { LogoTransfix } from 'transfix-ui/components/LogoTransfix'
import Typography from 'transfix-ui/core/Typography'
import { StringParam } from 'use-query-params'
import { callApi, ENDPOINTS } from 'utils/api/callApi'
import { useUserDetails } from 'utils/hooks/useUserDetails'
import { FP_HOME } from 'apps/fleet-planner/config'
import { UserContext } from 'apps/fleet-planner/context'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import {
  InvalidTokenMessage,
  LoginFormSchema,
  UsernamePasswordForm,
} from 'components/UsernamePasswordForm'
import { generateBaseUrl } from 'utils/generateAppDefaults'
import { useQueryParams } from 'utils/hooks'
import { storeUserData } from 'utils/userAuth'

interface IChildren {
  id: number
  parentUserId: number
}

interface IAuthSuccessResponse {
  approved?: boolean
  authToken: string
  cellPhone: string
  children: IChildren[]
  email: string
  firstName: string
  hasFleetPlannerAccess: boolean
  id: boolean
  lastName: string
  redirectToFp: boolean
  redirectToTms: boolean
  role: string
  transfixPhone: string
  status: number
  type: string
  uuid: string
}

export const Login = () => {
  const { trackEvent } = useFMSAnalytics()
  // Get userId to check if user is already logged in
  const { updateUserDetails, userDetails } = useUserDetails()
  const { id: userId } = userDetails || {}
  const { fetchUserData } = useContext(UserContext)
  const history = useHistory()
  const location = useLocation()

  const [error, setError] = useState<{
    type: INLINE_MESSAGE_VARIANTS
    message: string | JSX.Element
    dataTest?: string
  }>()

  // Handles redirection to the original destination or the FP homepage
  const { from } = (location.state as { from: { pathname: string } }) || {
    from: { pathname: FP_HOME },
  }

  // Email link authentication
  const { token } = useParams<{ token: string }>()
  const [{ redirect }] = useQueryParams<{ redirect?: string }>({
    redirect: StringParam,
  })

  const getUserSessionDetails = async (id: number) => {
    try {
      const res = await callApi({
        endpoint: ENDPOINTS.fleetPlannerUsers,
        group: `${id}/session_details`,
        includeXHeaderUserId: false,
      })
      return res
    } catch (e) {
      setError({
        type: INLINE_MESSAGE_VARIANTS.ALERT,
        message: 'Something went wrong, please try again.',
      })
      return e
    }
  }

  const onAuthSuccess = async (res: IAuthSuccessResponse) => {
    const { children = [] } = res

    if (res.status === 401) {
      throw new Error('Wrong Password: 401 returned')
    }

    await storeUserData({ ...res, app: 'fleet-planner' })
    if (children.length) {
      const userSessionDetailsRes = await getUserSessionDetails(children[0].id)
      await storeUserData({ ...userSessionDetailsRes, app: 'fleet-planner' })
    }

    await fetchUserData()

    if (typeof updateUserDetails === 'function') {
      await updateUserDetails()
    }
    // if user hasn't been redirected yet,
    // send them to the page they intended
    // to reach before being required to log in
    if (!redirect) {
      history.replace(from)
    }
  }

  useEffect(() => {
    if (token) {
      trackEvent('Login', 'VIEWED_LOGIN_WITH_TOKEN_PAGE')
      const authTokenLogin = async () => {
        try {
          const res = await callApi({
            endpoint: ENDPOINTS.signIn,
            method: 'POST',
            body: { userLogin: { resetPasswordToken: token } },
          })
          await onAuthSuccess(res)
        } catch (e) {
          setError({
            type: INLINE_MESSAGE_VARIANTS.ALERT,
            message: (
              <InvalidTokenMessage requestLoginUrl={generateBaseUrl('fms/login/request_link')} />
            ),
            dataTest: 'wrong-password-inline-message',
          })
        }
      }
      authTokenLogin()
    } else {
      trackEvent('Login', 'VIEWED_LOGIN_PAGE')
    }
  }, [token])

  const loginSubmitHandler = async ({ email, password }: LoginFormSchema) => {
    try {
      if (!email || !password) {
        setError({
          type: INLINE_MESSAGE_VARIANTS.WARNING,
          message: 'Whoops, you didn’t enter an email or password.',
          dataTest: 'empty-field-inline-message',
        })
      } else {
        setError(undefined)
        const res = await callApi({
          endpoint: ENDPOINTS.signIn,
          method: 'POST',
          body: { userLogin: { email, password } },
        })
        trackEvent('Login', 'LOGGED_IN')
        await onAuthSuccess(res)
      }
    } catch (e) {
      setError({
        type: INLINE_MESSAGE_VARIANTS.ALERT,
        message: (
          <InvalidTokenMessage requestLoginUrl={generateBaseUrl('fms/login/request_link')} />
        ),
        dataTest: 'wrong-password-inline-message',
      })
    }
  }

  // If user lands on the login page and is already identified
  // Redirect them to the expected page, or go to homepage
  if (userId) {
    if (redirect) {
      return <Redirect to={`/${redirect}`} />
    }
    return <Redirect to={FP_HOME} />
  }

  return (
    <div className='flex justify-center'>
      <div className='flex flex-column col-sm-12 col-lg-3'>
        <div className='flex-grow-1 tc ma8'>
          <LogoTransfix color='black' />
        </div>
        <Typography variant='h2' align='center'>
          Carrier Login
        </Typography>
        <p className='mb5 tc'>
          Need a Transfix Carrier account?
          <a className='b ml1' href={`${__ROOT__}/fms/new-onboarding/`}>
            Sign Up
          </a>
        </p>
        {error && (
          <InlineMessage
            className='mb5'
            fullWidth
            message={error.message}
            type={error.type}
            dataTest={error.dataTest}
          />
        )}
        <UsernamePasswordForm onSubmit={loginSubmitHandler} />
        <p className='tc mb5'>
          <Link to='/login/request_link?reset_password=true' className='btn btn-tertiary btn-large'>
            Reset password
          </Link>{' '}
          |{' '}
          <Link className='btn btn-tertiary btn-large' to='/login/request_link'>
            Request Login Link
          </Link>
        </p>
      </div>
    </div>
  )
}

export default Login
